import { CollectionContainersField } from "@/components/collection/CollectionContainersField"
import { SpeciesSelectInput } from "@/components/collection/SpeciesSelectInput"
import {
  saveCollectionContainers,
  useCollectionContainers,
  type CollectionContainerInput,
} from "@/hooks/useContainers"
import { useAuth } from "@/hooks/useAuth"
import { FullCollection, useCollection } from "@/hooks/useCollection"
import { usePhotosMutate } from "@/hooks/usePhotosMutate"
import { useCollectionUpdate } from "@/hooks/useCollectionUpdate"
import { useNetwork } from "@/hooks/useNetwork"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  MATERIAL_TYPES,
  MATERIAL_TYPE_OPTIONS,
  ROLE,
  toMaterialTypes,
  UpdateCollection,
} from "@nasti/common/types"
import { Button } from "@nasti/ui/button"
import { Input } from "@nasti/ui/input"
import { Label } from "@nasti/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@nasti/ui/popover"
import { PhenologyRangeInput } from "@nasti/ui/phenologyRangeInput"
import { CheckboxGroup } from "@nasti/ui/checkboxGroup"
import { Switch } from "@nasti/ui/switch"
import { Textarea } from "@nasti/ui/textarea"
import { cn } from "@nasti/ui/utils"
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { ChevronLeft, InfoIcon, X } from "lucide-react"
import { useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import * as z from "zod"
import { PhotosForm, PhotoChanges } from "@/components/common/PhotosForm"
import { AudiosForm, AudioChanges } from "@/components/common/AudiosForm"
import { useAudiosMutate } from "@/hooks/useAudiosMutate"
import { optionalPositiveNumber, stringToNumber } from "@nasti/common/utils"
import { PersonMultiSelectField } from "@/components/common/PersonMultiSelectField"
import { DurationPickerField } from "@/components/common/DurationPickerField"
import { useCurrentUserPerson } from "@/hooks/useCurrentUserPerson"
import { ExtraFieldsAccordion } from "@/components/common/ExtraFieldsAccordion"
import { useUnsavedChangesPrompt } from "@/hooks/useUnsavedChangesPrompt"
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog"
import { hasMediaChanges } from "@/lib/mediaChanges"

const collectionFields = z.object({
  species_id: z.preprocess(
    (val) => (val === "" ? null : val),
    z.string().nullable(),
  ),
  species_uncertain: z.boolean(),
  field_name: z
    .string()
    .nullable()
    .transform((val) => val || ""),
  specimen_collected: z.boolean(),
  description: z
    .string()
    .optional()
    .transform((val) => val || ""),
  containers: z
    .array(
      z.object({
        container_id: z.string().uuid("Select a container"),
        amount: optionalPositiveNumber,
      }),
    )
    .default([]),
  duration: z.string().nullable(),
  material_type: z.array(z.enum(MATERIAL_TYPES)).default([]),
  latitude: stringToNumber,
  longitude: stringToNumber,
  person_ids: z.array(z.string().uuid()).default([]),
  phenology_start: z.number().min(-100).max(100).nullable(),
  phenology_peak: z.number().min(-100).max(100).nullable(),
  phenology_end: z.number().min(-100).max(100).nullable(),
})

const schema = collectionFields
  .extend({
    // a collection has to record what material was collected
    material_type: z
      .array(z.enum(MATERIAL_TYPES))
      .min(1, "Select the material collected"),
  })
  .refine(
    (data) => Boolean(data.species_id) || data.field_name.trim().length > 0,
    {
      message: "Field name is required when no species is selected",
      path: ["field_name"],
    },
  )

type FormValues = z.infer<typeof schema>

const DEFAULT_VALUES: FormValues = {
  species_id: null,
  species_uncertain: false,
  field_name: "",
  latitude: null,
  longitude: null,
  specimen_collected: false,
  description: "",
  person_ids: [],
  phenology_start: null,
  phenology_peak: null,
  phenology_end: null,
  containers: [],
  duration: null,
  material_type: [],
}

export const Route = createFileRoute(
  "/_private/trips/$id/collections/$collectionId/edit",
)({
  component: CollectionForm,
})

function CollectionForm() {
  const { collectionId, id: tripId } = useParams({
    from: "/_private/trips/$id/collections/$collectionId/edit",
  })
  const collection = useCollection({ collectionId, tripId })
  // The join rows load separately, so hold off mounting the form until they
  // are here and it can be initialised with its real values.
  const { data: collectionContainers } = useCollectionContainers(collectionId)

  const navigate = useNavigate({
    from: "/trips/$id/collections/$collectionId/edit",
  })

  const handleBackClick = () => {
    navigate({
      to: "/trips/$id/collections/$collectionId",
      params: { id: tripId, collectionId },
    })
  }

  if (!collection)
    return (
      <div className="flex h-full w-full flex-col items-start">
        <h2 className="p-2 text-2xl">No collection available</h2>
        <Button
          className="flex w-full justify-start text-lg"
          variant="ghost"
          onClick={handleBackClick}
        >
          <ChevronLeft className="h-5 w-5" /> Back
        </Button>
      </div>
    )

  if (!collectionContainers) return null

  return (
    <CollectionFormReady
      collection={collection}
      collectionId={collectionId}
      tripId={tripId}
      initialContainers={collectionContainers.map(
        ({ container_id, amount }) => ({ container_id, amount }),
      )}
    />
  )
}

function CollectionFormReady({
  collection,
  collectionId,
  tripId,
  initialContainers,
}: {
  collection: FullCollection
  collectionId: string
  tripId: string
  initialContainers: CollectionContainerInput[]
}) {
  const { user, organisation, role } = useAuth()
  const currentUserPerson = useCurrentUserPerson(collection.organisation_id)

  const { mutateAsync: updateCollection } = useCollectionUpdate({ tripId })
  const { createPhotoMutation, updateCaptionMutation, deletePhotoMutation } =
    usePhotosMutate({
      entityId: collectionId,
      entityType: "collection",
      tripId,
    })
  const {
    createAudioMutation,
    deleteAudioMutation,
    updateCaptionMutation: updateAudioCaptionMutation,
  } = useAudiosMutate({
    entityId: collectionId,
    entityType: "collection",
    tripId,
  })

  const { isOnline } = useNetwork()

  const navigate = useNavigate({
    from: "/trips/$id/collections/$collectionId/edit",
  })

  const handleBackClick = () => {
    navigate({
      to: "/trips/$id/collections/$collectionId",
      params: { id: tripId, collectionId },
    })
  }

  // Unique ID: use provided or generate new
  const collectionIdRef = useRef<string>(collection.id)

  const initialPhotos = collection.photos ?? []
  // Photo state
  const [photoChanges, setPhotoChanges] = useState<PhotoChanges>({
    add: [],
    keep: initialPhotos,
  })

  const initialAudios = collection.audios ?? []
  // Audio state
  const [audioChanges, setAudioChanges] = useState<AudioChanges>({
    add: [],
    keep: initialAudios,
  })

  // parsed leniently - collections recorded before material type was required
  // still have to open in the form
  const defaultValues = collectionFields.parse({
    ...DEFAULT_VALUES,
    ...collection,
    containers: initialContainers,
    material_type: toMaterialTypes(collection.material_type),
    latitude: collection.locationCoord?.latitude ?? null,
    longitude: collection.locationCoord?.longitude ?? null,
  })

  // Form setup
  const {
    watch,
    setValue,
    register,
    handleSubmit: hookSubmit,
    formState: { isValid, isSubmitting, errors, isDirty },
    control,
  } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
  })

  const hasUnsavedChanges =
    isDirty ||
    hasMediaChanges(initialPhotos, photoChanges) ||
    hasMediaChanges(initialAudios, audioChanges)

  const { isPromptOpen, discardChanges, keepEditing, allowNavigation } =
    useUnsavedChangesPrompt({ hasUnsavedChanges })

  // Field name entry toggle
  const isFieldName =
    watch("specimen_collected") || Boolean(defaultValues.field_name)
  const [enterFieldName, setEnterFieldName] = useState(isFieldName)

  const handleSetIsSpecimenCollected = (val: boolean) => {
    setValue("specimen_collected", val)
    setEnterFieldName(val)
  }

  // Handlers
  const onFormSubmit = async (data: FormValues) => {
    if (!user || !organisation) throw new Error("Not logged in")
    if (!tripId) throw new Error("tripId must be supplied")

    const { latitude, longitude, person_ids, containers, ...rest } = data
    const locationPoint = `POINT(${longitude} ${latitude})`
    const filteredPersonIds = currentUserPerson?.id
      ? person_ids.filter((personId) => personId !== currentUserPerson.id)
      : person_ids

    const payload: UpdateCollection = {
      id: collectionIdRef.current,
      trip_id: tripId,
      organisation_id: organisation.id,
      created_by: user.id,
      created_at: new Date().toISOString(),
      location: locationPoint,
      ...rest,
      person_ids: filteredPersonIds,
    }
    if (!hasUnsavedChanges) {
      allowNavigation()
      return navigate({
        to: "/trips/$id/collections/$collectionId",
        params: { id: tripId, collectionId },
      })
    }

    const updatePromise = updateCollection(payload)
    if (isOnline) await updatePromise
    await saveCollectionContainers(collectionIdRef.current, containers)
    await Promise.all(
      photoChanges.add.map((photo) =>
        createPhotoMutation.mutateAsync(photo, { onError: console.error }),
      ),
    )
    await Promise.all(
      audioChanges.add.map((audio) =>
        createAudioMutation.mutateAsync(audio, { onError: console.error }),
      ),
    )
    // find which photos have been removed from the initial list
    const removedPhotos = initialPhotos.filter(
      (photo) => !photoChanges.keep.find((p) => p.id === photo.id),
    )
    const deletePhotoPromises = removedPhotos.map((photo) =>
      deletePhotoMutation.mutateAsync(photo.id, { onError: console.error }),
    )

    // for the remaining photos, update captions if they have changed
    const changedPhotos = photoChanges.keep.filter((kept) => {
      const existing = initialPhotos.find((p) => p.id === kept.id)
      return existing?.caption !== kept.caption
    })

    const changePhotoPromises = changedPhotos.map((photo) =>
      updateCaptionMutation.mutateAsync({
        photoId: photo.id,
        caption: photo.caption,
      }),
    )

    // removed + caption-changed audio
    const removedAudios = initialAudios.filter(
      (audio) => !audioChanges.keep.find((a) => a.id === audio.id),
    )
    const deleteAudioPromises = removedAudios.map((audio) =>
      deleteAudioMutation.mutateAsync(audio.id, { onError: console.error }),
    )
    const changedAudios = audioChanges.keep.filter((kept) => {
      const existing = initialAudios.find((a) => a.id === kept.id)
      return existing?.caption !== kept.caption
    })
    const changeAudioPromises = changedAudios.map((audio) =>
      updateAudioCaptionMutation.mutateAsync({
        audioId: audio.id,
        caption: audio.caption,
      }),
    )

    if (isOnline) {
      // do not await the add photo Promises - they're slower and can happen in parallel
      // await Promise.all(addPhotoPromises)
      await Promise.all(deletePhotoPromises)
      await Promise.all(changePhotoPromises)
      await Promise.all(deleteAudioPromises)
      await Promise.all(changeAudioPromises)
    }

    allowNavigation()
    return navigate({
      to: "/trips/$id/collections/$collectionId",
      params: { id: tripId, collectionId },
    })
  }

  const speciesId = watch("species_id")
  const [descriptionFocus, setDescriptionFocus] = useState(false)

  // You shouldn't be here
  if (collection.created_by !== user?.id && role !== ROLE.ADMIN) {
    navigate({
      to: "/trips/$id/collections/$collectionId",
      params: { id: tripId, collectionId },
    })
    return null
  }

  return (
    <div className="flex h-full flex-col">
      <h2 className="p-2 text-2xl">{"Edit Collection"}</h2>
      <form
        className="flex flex-1 flex-col justify-between"
        onSubmit={hookSubmit(onFormSubmit)}
      >
        <div className="space-y-4 overflow-y-auto px-2">
          <SpeciesSelectInput
            onSelectSpecies={(speciesId) =>
              setValue("species_id", speciesId, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            tripId={tripId}
            selectedSpeciesId={speciesId ?? undefined}
          />
          {enterFieldName && (
            <div>
              <Label>Specimen Name</Label>
              <div className="flex w-full items-center space-x-2">
                <Input
                  {...register("field_name")}
                  className="h-12 text-lg"
                  autoComplete="off"
                  tabIndex={1}
                  autoFocus
                />
                <Button
                  onClick={(e) => {
                    e.preventDefault()
                    setValue("field_name", "", { shouldValidate: true })
                  }}
                  className="h-12"
                  variant={"outline"}
                >
                  <X />
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <Switch {...register("species_uncertain")} />
            <Label>Species Uncertain?</Label>
            <Popover>
              <PopoverTrigger asChild>
                <InfoIcon className="h-5 w-5" />
              </PopoverTrigger>
              <PopoverContent>
                Toggle if not 100% certain or to enter a specimen name
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center space-x-2">
            <Controller
              control={control}
              name="specimen_collected"
              render={({ field: { value } }) => (
                <Switch
                  id="specimen_collected"
                  checked={value}
                  onCheckedChange={handleSetIsSpecimenCollected}
                />
              )}
            />
            <Label>Specimen Collected?</Label>
            <Popover>
              <PopoverTrigger asChild>
                <InfoIcon className="h-5 w-5" />
              </PopoverTrigger>
              <PopoverContent>
                Select if you physically collected the specimen
              </PopoverContent>
            </Popover>
          </div>

          <Controller
            control={control}
            name="person_ids"
            render={({ field }) => (
              <PersonMultiSelectField
                organisationId={collection.organisation_id}
                value={field.value}
                onChange={field.onChange}
                displayCurrentUser
                label="Collectors"
              />
            )}
          />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Latitude</Label>
              <Input
                {...register("latitude")}
                className={errors.latitude ? "border-amber-600" : ""}
              />
              {errors.latitude && (
                <p className="text-amber-600">{errors.latitude.message}</p>
              )}
            </div>
            <div>
              <Label>Longitude</Label>
              <Input
                {...register("longitude")}
                className={errors.longitude ? "border-amber-600" : ""}
              />
              {errors.longitude && (
                <p className="text-amber-600">{errors.longitude.message}</p>
              )}
            </div>
          </div>

          <div>
            <Controller
              control={control}
              name="material_type"
              render={({ field }) => (
                <CheckboxGroup
                  label="Material Collected"
                  size="lg"
                  options={MATERIAL_TYPE_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.material_type && (
              <div className="mt-1 text-sm text-amber-600">
                {errors.material_type.message}
              </div>
            )}
          </div>

          <Controller
            control={control}
            name="containers"
            render={({ field }) => (
              <CollectionContainersField
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <ExtraFieldsAccordion>
            <Controller
              control={control}
              name="duration"
              render={({ field }) => (
                <DurationPickerField
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              control={control}
              name="phenology_start"
              render={({ field: startField }) => (
                <Controller
                  control={control}
                  name="phenology_peak"
                  render={({ field: peakField }) => (
                    <Controller
                      control={control}
                      name="phenology_end"
                      render={({ field: endField }) => (
                        <PhenologyRangeInput
                          value={[
                            startField.value,
                            peakField.value,
                            endField.value,
                          ]}
                          onValueChange={([start, peak, end]) => {
                            startField.onChange(start)
                            peakField.onChange(peak)
                            endField.onChange(end)
                          }}
                        />
                      )}
                    />
                  )}
                />
              )}
            />
            <PhotosForm
              initialPhotos={initialPhotos}
              onPhotosChange={setPhotoChanges}
            />

            <div>
              <Label>Description</Label>
              <Textarea
                {...register("description")}
                className={cn(
                  "transition-all",
                  descriptionFocus ? "h-40" : "h-20",
                )}
                onFocus={() => setDescriptionFocus(true)}
                onBlur={() => setDescriptionFocus(false)}
              />
              <AudiosForm
                initialAudios={initialAudios}
                onAudiosChange={setAudioChanges}
              />
            </div>
          </ExtraFieldsAccordion>
        </div>

        <div className="flex space-x-2 border-t p-2">
          <Button
            className="w-full"
            variant="secondary"
            onClick={handleBackClick}
          >
            Cancel
          </Button>
          <Button
            className="w-full"
            type="submit"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
      <UnsavedChangesDialog
        open={isPromptOpen}
        onDiscard={discardChanges}
        onKeepEditing={keepEditing}
      />
    </div>
  )
}
